import React from 'react';
import SiteFooter from '../components/sections/SiteFooter';
import SubmitTipButton from '../components/SubmitTipButton';
import { Link } from 'react-router-dom';
import { ArrowLeft, Scale } from 'lucide-react';
import PageSeoTag from '../components/PageSeoTag';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl md:text-2xl font-black text-white mb-3 border-l-4 border-red-500 pl-3">
      {title}
    </h2>
    <div className="text-gray-300 text-sm md:text-base leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const Terms = () => {
  return (
    <div className="min-h-screen bg-black text-white" data-testid="terms-page">
      <PageSeoTag pageKey="terms" />
      <header className="bg-zinc-900/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-300 hover:text-white text-sm font-bold" data-testid="terms-back-home">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <span className="text-sm font-black tracking-widest text-red-400">TERMS OF USE</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/15 border border-red-500/40 mb-4">
            <Scale className="h-3.5 w-3.5 text-red-400" />
            <span className="text-[11px] font-black tracking-[0.2em] text-red-400 uppercase">
              The House Rules
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black mb-2">Terms of Use</h1>
          <p className="text-xs text-gray-500">Last updated: 1 June 2026</p>
        </div>

        <Section title="1. Who We Are">
          <p>
            outlawmedia.com.au is operated by Outlaw Media, an independent news outlet
            headquartered in Dubai, United Arab Emirates, reporting from Melbourne, Australia.
            Throughout these terms, "we", "us" and "our" refer to Outlaw Media; "you" and "your"
            refer to any visitor to this site.
          </p>
        </Section>

        <Section title="2. Acceptance of Terms">
          <p>
            By accessing outlawmedia.com.au, our content, or any service offered here, you agree to be
            bound by these Terms. If you do not agree, you must not use this site.
          </p>
        </Section>

        <Section title="3. 18+ Restricted Content">
          <p>
            The "Explicit" / "18+ Gangland Videos" section contains graphic and confronting material.
            By entering that section you confirm that you are at least 18 years of age. We may verify
            your age in line with Australian law. Content in this section is provided for journalistic,
            educational, and public-interest purposes only.
          </p>
        </Section>

        <Section title="4. Editorial Independence">
          <p>
            All content published on this site is the work and opinion of Outlaw Media. We are
            independently owned and not affiliated with any political party, government body, or
            mainstream media outlet. Sponsored placements are clearly labelled "Sponsored" or
            "We Recommend".
          </p>
        </Section>

        <Section title="6. Comments & User-Submitted Text">
          <p>
            Outlaw Media allows readers to post anonymous comments and replies on articles and videos.
            By posting, you agree to the following:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>You will not post content that is unlawful, defamatory, harassing, threatening, or
              knowingly false.</li>
            <li>You will not post identifying details of protected persons, minors, or witnesses.</li>
            <li>You will not use the comments system to promote spam, scams, or malware.</li>
            <li>We reserve the absolute right to remove, redact or hide any comment at our discretion,
              with or without notice.</li>
          </ul>
          <p>
            <strong className="text-white">Logging & abuse prevention:</strong> when you submit a
            comment, reply, like, or newsletter signup, we automatically record your IP address, a
            timestamp, and your browser&apos;s user-agent string alongside the submission. This
            information is retained solely for the purposes of (a) enforcing these Terms, (b) blocking
            spammers, scanners and automated abuse of the site, and (c) complying with lawful requests
            from Victoria Police, the Australian Federal Police, or a court order. IP addresses are
            not displayed publicly and are never shared with other users. Attempts to probe, flood, or
            scrape the comments system will result in an automatic and permanent block from Outlaw
            Media.
          </p>
        </Section>

        <Section title="7. Tips & Anonymous Submissions">
          <p>
            When you submit a tip via the &ldquo;Submit a Tip&rdquo; feature, you grant us permission
            to use the information for our reporting. By submitting a tip you confirm:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>You will not submit content that is unlawful, defamatory, or knowingly false.</li>
            <li>You understand we may verify, edit, or decline to publish information you provide.</li>
            <li>You have the right to share any media you submit.</li>
          </ul>
          <p className="mt-4">
            <strong className="text-white">Information we retain on every tip.</strong> Although
            tip submissions are treated as confidential and are not published with identifying
            metadata, Outlaw Media retains a private forensic record of every submission for
            editorial verification, abuse prevention, and lawful disclosure. This record includes
            your IP address, internet service provider, approximate geolocation (city / region /
            country), the exact time of submission, your browser&apos;s user-agent string, device
            type, operating system, screen resolution, timezone, language settings, and other
            information your browser transmits by default with every request to any website.
          </p>
          <p className="mt-4">
            This information is <strong className="text-white">never displayed publicly, sold,
            or shared with advertisers or other third parties.</strong> It is retained
            <em> solely </em>
            for the purposes of (a) verifying tips and preventing hoaxes, (b) blocking spammers,
            scanners, and impersonators, and (c) complying with lawful requests from Victoria
            Police, the Australian Federal Police, the eSafety Commissioner, or a court order.
            If you have submitted a tip and would like your forensic record deleted, contact
            ryan@outlawmedia.com.au and we will honour the request within 30 days unless we
            are legally required to retain it.
          </p>
        </Section>

        <Section title="8. Use of Leaked Documents (The Machete Bin Files)">
          <p>
            Documents and material published by Outlaw Media as part of independent investigations
            remain the editorial work of Outlaw Media. Other media outlets that reproduce, quote, or
            reference our investigations must clearly credit Outlaw Media as the source. Failure to do
            so may result in legal action.
          </p>
        </Section>

        <Section title="9. Newsletter">
          <p>
            By subscribing to our newsletter you consent to receive emails from us. You may unsubscribe
            at any time via the link in any email or by contacting us.
          </p>
        </Section>

        <Section title="10. Disclaimer of Warranties">
          <p>
            The site and its content are provided on an &ldquo;as is&rdquo; basis. We make reasonable
            efforts to ensure accuracy, but make no warranty that content is error-free, complete, or
            current.
          </p>
        </Section>

        <Section title="11. Limitation of Liability">
          <p>
            To the maximum extent permitted by Australian law, Outlaw Media will not be liable for any
            indirect, incidental, or consequential damages arising out of your use of this site.
          </p>
        </Section>

        <Section title="12. Governing Law">
          <p>
            These Terms are governed by the laws of Victoria, Australia. Any disputes shall be subject
            to the exclusive jurisdiction of the courts of Victoria.
          </p>
        </Section>

        <Section title="13. Contact">
          <p>
            Questions about these Terms? Email{' '}
            <a href="mailto:ryan@outlawmedia.com.au" className="text-red-400 underline">ryan@outlawmedia.com.au</a>.
          </p>
        </Section>
      </main>

      <SiteFooter />
      <SubmitTipButton />
    </div>
  );
};

export default Terms;
